import DappPortalSDK from '@linenext/dapp-portal-sdk'
import { ChakraProvider, useToast, Box, useDisclosure } from '@chakra-ui/react';
import * as React from 'react';
import './styles/global.css';
import { theme } from './theme';
import { GameList } from './components/GameList';
import { Button, Text } from '@chakra-ui/react';
import { liff } from '@line/liff';
import { Helmet } from 'react-helmet-async';
import { FC, ReactElement, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { PowerRewardModal } from './components/PowerRewardModal';
import { submitPowerReward } from './services/powerRewards';
import { useSearchParams } from 'react-router-dom';
import { track } from '@vercel/analytics';
import { FamilyDappBanner } from './components/FamilyDappBanner';
import { Copyright } from './components/Copyright';

interface LiffAppProps {
  liff: typeof liff;
}

interface NFT {
  name: string;
  id?: string;
  tokenId: string;
  productCode?: string;
  isFreePlay?: boolean;
}

export const LiffApp: FC<LiffAppProps> = ({ liff }): ReactElement => {
  const { t } = useTranslation();
  const [, setProvider] = React.useState<any>(null);
  const [address, setAddress] = React.useState<string>();
  const [isConnected, setIsConnected] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const pictureUrl = liff.getDecodedIDToken()?.picture;
  const name = liff.getDecodedIDToken()?.name;
  const [isCopied, setIsCopied] = React.useState(false);
  const [userId, setUserId] = React.useState<string | null>(null);
  const toast = useToast();
  const [powerReward, setPowerReward] = useState<{ amount: number; reason: 'connect' | 'play' | 'share' } | null>(null);
  const { isOpen: isPowerRewardOpen, onOpen: onPowerRewardOpen, onClose: onPowerRewardClose } = useDisclosure();
  const [searchParams] = useSearchParams();
  const isDappPortal = searchParams.get('from') === 'dappportal.io';
  const [gbpBalance, setGbpBalance] = useState<number | null>(null);

  const fetchGbpBalance = async (walletAddress: string) => {
    try {
      const response = await fetch(`https://kaiko.retrofun.club/address/${walletAddress}`, {
        headers: {
          'Authorization': 'Bearer Token b5dc1822-6fdf-4be4-8671-d8c211ea2199'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGbpBalance(data.balance);
      }
    } catch (error) {
      console.error('Error fetching GBP balance:', error);
    }
  };

  const updateWalletAddress = async (userId: string, walletAddress: string) => {
    try {
      const response = await fetch('https://kaiko.retrofun.club/wallet', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer Token b5dc1822-6fdf-4be4-8671-d8c211ea2199'
        },
        body: JSON.stringify({
          user_id: userId,
          wallet_address: walletAddress
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update wallet address: ${response.status}`);
      }

      const data = await response.json();
      console.log('Wallet address updated:', data);
      return true;
    } catch (error) {
      console.error('Error updating wallet address:', error);
      return false;
    }
  };

  const handleConnect = async () => {
    if (isDappPortal) {
      liff.getProfile().then(async (profile) => {
        const success = await submitPowerReward({
          userId: profile.userId,
          amount: 100,
          reason: 'connect',
          timestamp: new Date().toISOString(),
          walletAddress: address
        });
        
        if (success) {
          setPowerReward({ amount: 100, reason: 'connect' });
          onPowerRewardOpen();
        }
      });
    }
  };

  const connectWallet = async () => {
    try {
      const sdk = await DappPortalSDK.init({ clientId: '452f1740-f518-4fa7-aa68-3bfee613cb94' });
      const provider = sdk.getWalletProvider();
      setProvider(provider);

      const accounts = await provider.request({ method: 'kaia_requestAccounts' }) as string[];

      if(accounts && accounts.length > 0) {
        const newAddress = accounts[0];
        setAddress(newAddress);
        setIsConnected(true);
        Cookies.set('isConnected', 'true', { expires: 365 });
        
        // userId가 있을 때는 LINE 사용자로 지갑 주소 업데이트, 없을 때는 외부 사용자로 등록
        if (userId) {
          await updateWalletAddress(userId, newAddress);
        } else {
          try {
            const response = await fetch('https://kaiko.retrofun.club/external/register', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                wallet_address: newAddress
              })
            });
            
            if (!response.ok) {
              console.error('Failed to register external user:', response.status);
            } else {
              console.log('External user registered successfully');
            }
          } catch (error) {
            console.error('Error registering external user:', error);
          }
        }
        
        fetchGbpBalance(newAddress);
        track('WalletConnect', {
          address: newAddress,
          provider: 'Unifi'
        });
      }
    } catch (error) {
      console.error('Failed to connect wallet:', error);
    }
  };

  const disconnectWallet = () => {
    track('WalletDisconnect', { 
      address: address || ''
    });
    setAddress(undefined);
    setIsConnected(false);
    setProvider(null);
    setIsOpen(false);
    setGbpBalance(null);
    Cookies.remove('isConnected');
  };

  useEffect(() => {
    const init = async () => {
      if (liff.isInClient() || searchParams.get('debug') === 'true') {
        if (!liff.isLoggedIn()) {
          liff.login();
        }
        liff.getProfile()
          .then(async (profile) => {
            setUserId(profile.userId);
            handleConnect();
            const inviterAddress = searchParams.get('inviterAddress');
            if (inviterAddress && !Cookies.get('inviterRegistered')) {
              try {
                const response = await fetch('https://kaiko.retrofun.club/inviter', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    user_id: profile.userId,
                    inviter_address: inviterAddress
                  })
                });
                
                if (response.status === 200 || response.status === 409) {
                  Cookies.set('inviterRegistered', 'true', { expires: 365 });
                }
              } catch (error) {
                console.error('Error submitting inviter:', error);
              }
            }
          })
          .catch((err) => {
            console.log("error", err);
          });
      }

      const isConnectedCookie = Cookies.get('isConnected');
      if (isConnectedCookie === 'true') {
        await connectWallet();
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (address) {
      fetchGbpBalance(address);
    }
  }, [address]);

  useEffect(() => {
    const handleScreenshot = async (event: MessageEvent) => {
      if (event.data.type === 'SCREENSHOT') {
        try {
          const base64Data = event.data.data;
          const blob = await (await fetch(base64Data)).blob();
          const productCode = event.data.productCode || 'unknown_game'; // Get product code from event data
          const file = new File([blob], `screenshot_${productCode}.png`, { type: blob.type });

          if (liff.isInClient()) {
            // Check last screenshot time from cookies
            const lastScreenshotTime = Cookies.get('lastScreenshotTime');
            const now = Date.now();
            const COOLDOWN_MS = 3000; // 3 second cooldown between screenshots

            if (lastScreenshotTime && (now - parseInt(lastScreenshotTime)) < COOLDOWN_MS) {
              toast({
                title: t('messages.screenshotLimit'),
                description: t('messages.pleaseWait'),
                status: "warning", 
                duration: 2000,
                isClosable: true,
                position: "top",
              });
              return;
            }

            const formData = new FormData();
            formData.append('image', base64Data.split(',')[1]);
            formData.append('key', '2d0ed7ad91952b2841785d005575cf98');
            formData.append('name', `retro_fun_${productCode}_screenshot`); // Set custom name for imgBB
            const response = await fetch('https://api.imgbb.com/1/upload', {
              method: 'POST',
              body: formData
            });

            const result = await response.json();
            if (result.data && result.data.url) {
              await liff.sendMessages([
                {
                  type: 'image',
                  originalContentUrl: result.data.url,
                  previewImageUrl: result.data.url
                }
              ]);

              // Update last screenshot time in cookies
              Cookies.set('lastScreenshotTime', now.toString());

              track('ScreenshotShare', { 
                productCode: productCode,
                success: true
              });

              toast({
                title: t('messages.screenshotShared'),
                status: "success",
                duration: 2000,
                isClosable: true,
                position: "top",
              });
            } else {
              throw new Error('Failed to upload image to ImgBB');
            }
          } else if (navigator.share) {
            await navigator.share({
              title: 'Retrofun',
              text: 'Check out this screenshot from Retrofun!',
              files: [file],
            });
          } else {
            await navigator.clipboard.write([
              new ClipboardItem({
                [blob.type]: blob
              })
            ]);
            toast({
              title: t('messages.screenshotCopied'),
              status: "success",
              duration: 2000,
              isClosable: true,
              position: "top",
            });
          }
        } catch (error) {
          console.error('Error sharing screenshot:', error);
          toast({
            title: t('messages.screenshotError'),
            description: String(error),
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
        }
      }
    };

    window.addEventListener('message', handleScreenshot);
    return () => window.removeEventListener('message', handleScreenshot);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liff, toast]);

  useEffect(() => {
    console.log('Address updated:', address);
  }, [address]);

  interface ConnectButtonProps {
    size: "sm" | "md" | "lg";
  }

  const ConnectButton: React.FC<ConnectButtonProps> = ({ size }) => {
    return (
      <Button
        size={size}
        display="flex"
        alignItems="center"
        gap={2}
        onClick={isConnected ? () => setIsOpen(!isOpen) : connectWallet}
        boxShadow="4px 4px 4px rgba(43, 43, 47, 0.25)"
        bg={isConnected ? (isOpen ? "gray.100" : "white") : "white"}
        color="black"
        _hover={{ bg: isConnected ? (isOpen ? "gray.200" : "gray.50") : "gray.50" }}
        _active={{ bg: isConnected ? "gray.100" : "gray.100" }}
      >
        {isConnected && (
          <img
            src={pictureUrl || process.env.PUBLIC_URL + "/base_profile.png"}
            alt={t('profile.baseProfile')}
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "12px"
            }}
          />
        )}
        {!isConnected && (
          <img
            src={process.env.PUBLIC_URL + "/icon_unifi.svg"}
            alt={t('profile.unifi')}
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "12px"
            }}
          />
        )}
        {isConnected ? (
          name && name.length > 0 && <Text display={{ base: "none", md: "block" }}>{name.length > 6 ? name.slice(0, 6) + "…" : name}</Text>
        ) : (
          t('buttons.connect')
        )}
      </Button>
    );
  };

  const renderConnectButton = () => {
    const handleShare = async () => {
      if (liff.isInClient() && userId && address) {
        try {
          const result = await liff.shareTargetPicker([
            {
              "type": "flex",
              "altText": "Retrofun Invitation",
              "contents": {
                "type": "bubble",
                "hero": {
                  "type": "image",
                  "url": "https://gameboy-liff.retrofun.club/retro_opengraph.png",
                  "size": "full",
                  "aspectRatio": "120:63",
                  "aspectMode": "cover",
                },
                "body": {
                  "type": "box",
                  "layout": "vertical",
                  "contents": [
                    {
                      "type": "text",
                      "text": "Retrofun",
                      "weight": "bold",
                      "size": "xl",
                    },
                    {
                      "type": "text",
                      "text": t('share.message'),
                      "wrap": true
                    }
                  ]
                },
                "footer": {
                  "type": "box",
                  "layout": "vertical",
                  "spacing": "sm",
                  "contents": [
                    {
                      "type": "button",
                      "style": "primary",
                      "height": "sm",
                      "action": {
                        "type": "uri",
                        "label": t('share.button'),
                        "uri": "https://liff.line.me/2006689873-VxpWRda5?inviterAddress=" + address
                      }
                    },
                  ],
                  "flex": 0
                }
              }
            }
          ]);
          if (result) {
            track('InviteShare', { 
              success: true,
              address: address 
            });
            
            const success = await submitPowerReward({
              userId: userId,
              amount: 200,
              reason: 'share',
              timestamp: new Date().toISOString(),
              walletAddress: address
            });
            if (success) {
              track('PowerReward', { 
                amount: 200,
                reason: 'share',
                address: address
              });
              setPowerReward({ amount: 200, reason: 'share' });
              onPowerRewardOpen();
            }
          }
        } catch (error) {
          console.error('Failed to share:', error);
        }
      }
    };

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
        top: 0,
        right: 0,
        zIndex: 1000
      }}>
        <ConnectButton size="md" />
        {isOpen && (
          <div
            style={{
              position: "absolute",
              right: "10px",
              top: "70px",
              background: "white",
              padding: "16px",
              borderRadius: "12px",
              boxShadow: "4px 4px 4px rgba(43, 43, 47, 0.25)",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              zIndex: 1000,
              minWidth: "240px"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <img
                src={pictureUrl || process.env.PUBLIC_URL + "/base_profile.png"}
                alt={t('profile.baseProfile')}
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "24px"
                }}
              />
              <div>
                <div style={{ fontWeight: "bold", textAlign: "left", maxWidth: "280px" }}>{name}</div>
                <div style={{ fontSize: "14px", opacity: 0.7, textAlign: "left" }}>
                  {address?.slice(0, 6)}...{address?.slice(-4)}
                </div>
                {gbpBalance !== null && (
                  <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "4px" }}>
                    <img
                      src={process.env.PUBLIC_URL + "/power210.svg"}
                      alt="GBP"
                      style={{
                        width: "16px",
                        height: "16px"
                      }}
                    />
                    <span style={{ fontSize: "14px" }}>{Math.floor(gbpBalance)} GBP</span>
                  </div>
                )}
              </div>
            </div>
            <Button
              size="sm"
              width="100%"
              onClick={() => {
                navigator.clipboard.writeText(address || "");
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 2000);
              }}
            >
              {isCopied ? t('buttons.copied') : t('buttons.copyAddress')}
            </Button>
            {liff.isInClient() && (
              <Button
                size="sm"
                width="100%"
                onClick={handleShare}
                bg="linegreen.500"
                color="white"
                _hover={{ bg: "green.600" }}
                _active={{ bg: "green.700" }}
              >
                {t('buttons.share')}
              </Button>
            )}
            <Button
              size="sm"
              width="100%"
              onClick={isConnected ? disconnectWallet : connectWallet}
              bg={isConnected ? "red.500" : "blue.500"}
              color="white"
              _hover={{ bg: isConnected ? "red.600" : "blue.600" }}
              _active={{ bg: isConnected ? "red.700" : "blue.700" }}
            >
              {isConnected ? t('buttons.disconnect') : t('buttons.connect')}
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderEmptyStateButton = () => {
    return (
      <Box mt={10} maxWidth={400} mr="auto" ml="auto">
        <ConnectButton size="lg" />
      </Box>
    );
  };

  const handleGamePlay = async (nft: NFT) => {
    track('GamePlay', { 
      tokenId: String(nft.tokenId),
      productCode: String(nft.productCode ?? 'unknown'),
      isFreePlay: Boolean(nft.isFreePlay)
    });

    if (isDappPortal && nft.isFreePlay) {
      liff.getProfile().then(async (profile) => {
        const success = await submitPowerReward({
          userId: profile.userId,
          amount: 100,
          reason: 'play',
          timestamp: new Date().toISOString(),
          walletAddress: address
        });
        
        if (success) {
          setPowerReward({ amount: 100, reason: 'play' });
          onPowerRewardOpen();
        }
      });
    }
  };

  return (
    <ChakraProvider theme={theme}>
      <Helmet>
        <title>Retrofun │ Unifi Apps</title>
        {liff.isInClient() && (
          <meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=no" />
        )}
      </Helmet>
      <PowerRewardModal 
        isOpen={isPowerRewardOpen}
        onClose={onPowerRewardClose}
        amount={powerReward?.amount || 0}
        reason={powerReward?.reason || 'connect'}
      />
      <GameList 
        address={address}
        chainId="8217"
        isConnected={isConnected}
        todaysGameSeed={userId ? userId : address}
        renderConnectButton={renderConnectButton}
        renderEmptyStateButton={renderEmptyStateButton}
        onGamePlay={handleGamePlay}
      />
      {isConnected && (
        <Box p={4} maxW="container.md" mx="auto">
          <FamilyDappBanner />
        </Box>
      )}
      <Copyright />
    </ChakraProvider>
  );
};
